<template>
  <SoDialog
    v-model="value"
    :card-title="title"
    :loading="!!changesQuery?.isFetching"
    data-testid="feature-changes-dialog"
  >
    <SoAlert
      v-if="changesQuery?.isError"
      type="error"
      title="Failed to load change history"
      data-testid="feature-changes-error"
    />
    <FeatureChangesList
      v-else-if="changesQuery"
      :changes="changesQuery.data"
      :show-feature-name="featureId === null"
    />
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import { QueryResult } from "@/api/utils/types"
  import { IFeatureChange } from "@/models/admin/featureChange/featureChange.types"
  import { IFeature } from "@/models/admin/feature/feature.types"
  import FeatureChangesList from "./FeatureChangesList.vue"

  /**
   * Change history from be-identity's feature change audit. With a featureId it
   * shows that flag's history; with featureId = null it shows recent changes
   * across all flags.
   */
  export default defineComponent({
    components: { SoDialog, SoAlert, FeatureChangesList },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      featureId: {
        type: Number as PropType<number | null>,
        default: null,
      },
      featureName: {
        type: String,
        default: "",
      },
      // Saved flags, for the state after each flag's latest change.
      currentFeatures: {
        type: Array as PropType<IFeature[]>,
        default: () => [],
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        changesQuery: null as QueryResult<IFeatureChange[]> | null,
      }
    },
    computed: {
      value: {
        get(): boolean {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      title(): string {
        return this.featureId === null
          ? "Recent feature flag changes"
          : `History: ${this.featureName}`
      },
    },
    watch: {
      modelValue: {
        immediate: true,
        handler(open: boolean) {
          if (!open) return
          this.changesQuery = FeaturesService.getFeatureChanges(
            this.featureId,
            this.currentFeatures,
          )
          // Cached per key, so refetch to pick up changes made since it was last opened.
          if (this.changesQuery.isFetched) this.changesQuery.refetch()
        },
      },
    },
  })
</script>
